import { AlertTriangle } from "lucide-react";

import {
  createPreviewActions,
  ShareablePreview,
} from "@/core/components/shared/preview-action";
import type { ApprovalRequestDto } from "@/features/admin/api/dto";
import { useAdminTranslation } from "@/features/admin/i18n/use-admin-translation";

/**
 * Preview d'approbation d'une demande (règle projet : tout submit passe par
 * ShareablePreview). Le récapitulatif affiche la demande complète (payload
 * formaté inclus) ; le confirm déclenche la chaîne approbation → exécution
 * de l'action protégée → acquittement (gérée par la page détail).
 *
 * Règle des quatre yeux : quand l'utilisateur courant est le demandeur,
 * le confirm est désactivé et un avertissement s'affiche (422 côté serveur
 * sinon).
 */
export function ApprovalRequestDecidePreview({
  approvalRequest,
  open,
  onOpenChange,
  onConfirm,
  pending,
  selfApproval,
}: {
  approvalRequest: ApprovalRequestDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  pending: boolean;
  /** Vrai si l'utilisateur courant est le demandeur (auto-approbation). */
  selfApproval: boolean;
}) {
  const { t } = useAdminTranslation();

  if (!open) return null;

  let payloadPretty = approvalRequest.payload;
  try {
    payloadPretty = JSON.stringify(JSON.parse(approvalRequest.payload), null, 2);
  } catch {
    /* payload non-JSON : affiché tel quel */
  }

  const actions = createPreviewActions({
    onConfirm,
    onCancel: () => onOpenChange(false),
    labels: { confirm: t("admin.approvals.decide.approve") },
    t,
    confirmLoading: pending,
    withIndicatorOnConfirm: true,
  });
  if (selfApproval) {
    actions.forEach((action) => {
      if (action.variant === "green") action.disabled = true;
    });
  }

  return (
    <ShareablePreview
      data={{ ...approvalRequest, payload: payloadPretty }}
      title={t("admin.approvals.decide.approveTitle")}
      description={t("admin.approvals.decide.approveDescription")}
      mode="user"
      defaultOpen
      onOpenChange={(isOpen) => {
        if (!isOpen && !pending) onOpenChange(false);
      }}
      maxHeight="90vh"
      customView={
        selfApproval
          ? () => (
              <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning-muted px-3 py-2 text-sm text-warning">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>{t("admin.approvals.decide.selfApprovalHint")}</span>
              </div>
            )
          : undefined
      }
      actions={actions}
    />
  );
}
